import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  List,
  Popconfirm,
  message,
  Space,
  Empty,
  Card,
  Spin,
  Divider,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MinusCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { preStartTemplateService, inspectionService, formService } from "@iso/services";

// Manage per-property custom pre-start inspection checklist templates. Two views
// live in one modal: a list of existing templates, and an editor (section /
// item builder) for creating or editing one. Assets can then be assigned a
// template on the asset edit form; unassigned assets use the default checklist.
const ChecklistTemplatesModal = (props) => {
  const { visible, onCancel, propertyId } = props;
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [preStartForms, setPreStartForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("list"); // "list" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);

  const loadTemplates = () => {
    if (!propertyId) return;
    setLoading(true);
    Promise.all([
      preStartTemplateService.getList(propertyId),
      formService.getList(propertyId, { form_purpose: 'pre_starts' }),
    ])
      .then(([tplRes, formRes]) => {
        if (tplRes && tplRes.code === "0000") setTemplates(tplRes.templates || []);
        if (formRes && formRes.code === "0000") setPreStartForms(formRes.forms || []);
      })
      .catch(() => message.error("Failed to load checklist templates"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible) {
      setMode("list");
      setEditingId(null);
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, propertyId]);

  // Apply the staged values once the editor form has actually mounted (setting
  // fields synchronously right after setMode would hit a disconnected instance).
  useEffect(() => {
    if (mode === "edit" && pendingValues) {
      form.setFieldsValue(pendingValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pendingValues]);

  const toFormShape = (sections) =>
    (sections || []).map((s) => ({
      section: s.section,
      items: (s.items || []).map((label) => ({ label })),
    }));

  const startCreate = () => {
    setEditingId(null);
    // Seed a new template from the default checklist so the user tweaks rather
    // than starts from a blank slate.
    inspectionService
      .getChecklist()
      .then((res) => {
        const sections = (res && res.checklist) || [];
        setPendingValues({
          name: "",
          sections: sections.length
            ? toFormShape(sections)
            : [{ section: "", items: [{ label: "" }] }],
        });
        setMode("edit");
      })
      .catch(() => {
        setPendingValues({
          name: "",
          sections: [{ section: "", items: [{ label: "" }] }],
        });
        setMode("edit");
      });
  };

  const startEdit = (tpl) => {
    setEditingId(tpl.id);
    setPendingValues({ name: tpl.name, sections: toFormShape(tpl.items) });
    setMode("edit");
  };

  const handleDelete = (id) => {
    preStartTemplateService
      .destroy(id)
      .then((res) => {
        if (res && res.code === "0000") {
          message.success("Template deleted");
          loadTemplates();
        } else {
          message.error((res && res.message) || "Failed to delete template");
        }
      })
      .catch(() => message.error("Failed to delete template"));
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      // Convert the editor shape back to storage shape:
      // [{ section, items: [label, ...] }, ...]
      const items = (values.sections || [])
        .map((s) => ({
          section: (s.section || "").trim(),
          items: (s.items || [])
            .map((it) => (it && it.label ? it.label.trim() : ""))
            .filter(Boolean),
        }))
        .filter((s) => s.section && s.items.length);
      if (!items.length) {
        message.warning("Add at least one section with one item");
        return;
      }
      const payload = {
        pre_start_template: {
          property_id: propertyId,
          name: (values.name || "").trim(),
          items,
        },
      };
      setSaving(true);
      preStartTemplateService
        .storeOrUpdate(payload, editingId)
        .then((res) => {
          if (res && res.code === "0000") {
            message.success(editingId ? "Template updated" : "Template created");
            setPendingValues(null);
            setMode("list");
            loadTemplates();
          } else {
            message.error((res && res.message) || "Failed to save template");
          }
        })
        .catch(() => message.error("Failed to save template"))
        .finally(() => setSaving(false));
    });
  };

  const footer =
    mode === "list"
      ? [
          <Button key="close" onClick={onCancel}>
            Close
          </Button>,
        ]
      : [
          <Button key="back" onClick={() => setMode("list")}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>
            Save Template
          </Button>,
        ];

  return (
    <Modal
      title="Pre-start Checklist Templates"
      visible={visible}
      onCancel={onCancel}
      width={720}
      footer={footer}
    >
      {mode === "list" ? (
        <Spin spinning={loading}>
          <div style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={startCreate}
              disabled={!propertyId}
            >
              New Template
            </Button>
          </div>
          {templates.length ? (
            <List
              dataSource={templates}
              renderItem={(tpl) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => startEdit(tpl)}
                    >
                      Edit
                    </Button>,
                    <Popconfirm
                      title="Delete this template? Assets using it will fall back to the default checklist."
                      onConfirm={() => handleDelete(tpl.id)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Button type="link" danger icon={<DeleteOutlined />}>
                        Delete
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={tpl.name}
                    description={`${(tpl.items || []).length} section(s), ${(tpl.items || []).reduce(
                      (n, s) => n + (s.items || []).length,
                      0
                    )} item(s)`}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description={
              preStartForms.length
                ? "No custom templates yet. Assets use the default checklist. Pre-start forms (below) can also be assigned."
                : "No custom templates yet. Assets use the default checklist."
            } />
          )}
          {preStartForms.length > 0 && (
            <>
              <Divider orientation="left" style={{ fontSize: 13, color: "rgba(0,0,0,0.45)" }}>
                Pre-start Forms
              </Divider>
              <List
                dataSource={preStartForms}
                renderItem={(f) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<FileTextOutlined style={{ fontSize: 18, color: "#0B2A1B", marginTop: 2 }} />}
                      title={
                        <span>
                          {f.name}
                          {f.primary === "Public" && <Tag color="green" style={{ marginLeft: 8, fontSize: 11 }}>Primary</Tag>}
                        </span>
                      }
                      description={`Form · ${f.form_category || "Pre-Starts"}`}
                    />
                  </List.Item>
                )}
              />
            </>
          )}
        </Spin>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, whitespace: true, message: "Enter a template name" }]}
          >
            <Input placeholder="e.g. Tractor daily check" />
          </Form.Item>
          <Form.List name="sections">
            {(sectionFields, { add: addSection, remove: removeSection }) => (
              <>
                {sectionFields.map(({ key, name: sName, ...sRest }, sIdx) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    title={`Section ${sIdx + 1}`}
                    extra={
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeSection(sName)}
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Form.Item
                      {...sRest}
                      name={[sName, "section"]}
                      label="Section name"
                      rules={[{ required: true, whitespace: true, message: "Section name required" }]}
                    >
                      <Input placeholder="e.g. Engine & Fluids" />
                    </Form.Item>
                    <Form.List name={[sName, "items"]}>
                      {(itemFields, { add: addItem, remove: removeItem }) => (
                        <>
                          {itemFields.map(({ key: iKey, name: iName, ...iRest }) => (
                            <Space
                              key={iKey}
                              align="baseline"
                              style={{ display: "flex", marginBottom: 8 }}
                            >
                              <Form.Item
                                {...iRest}
                                name={[iName, "label"]}
                                rules={[{ required: true, whitespace: true, message: "Item required" }]}
                                style={{ marginBottom: 0, width: 480 }}
                              >
                                <Input placeholder="Checklist item" />
                              </Form.Item>
                              <MinusCircleOutlined onClick={() => removeItem(iName)} />
                            </Space>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => addItem({ label: "" })}
                            icon={<PlusOutlined />}
                            block
                          >
                            Add item
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => addSection({ section: "", items: [{ label: "" }] })}
                  icon={<PlusOutlined />}
                  block
                >
                  Add section
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Modal>
  );
};

export default ChecklistTemplatesModal;
